/**
 * Export half of Settings → Backup & Transfer → Connections.
 *
 * The reassurance line and the passphrase copy are the feature, not decoration.
 * A user is about to write a file they may email, and the two questions they
 * will ask — "are my passwords in this?" and "what IS in it, then?" — are both
 * answered next to the button, in both states of the toggle.
 */
import { useMemo, useState } from 'react';
import { Download, ShieldCheck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { toast } from '../../../components/ui/toaster';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import {
  MIN_TRANSFER_PASSPHRASE_LENGTH,
  describeTransport,
} from '../../../types/connectionTransfer';
import { useConnectionStore } from '../store/connectionStore';
import { countBucket } from '../connection-import/connectionImportUtils';
import {
  CONNECTIONS_EXPORT_CAVEAT,
  connectionsFileName,
  exportConnectionsToFile,
} from './connectionTransferApi';
import { ConnectionPickList, type PickRow } from './ConnectionPickList';
import { ExportPassphraseFields } from './PassphraseFields';
import { derivePassphrase, useExportPassphraseForm } from './exportPassphraseForm';
import { safeConnectionName, safeConnectionTarget } from '../../../types/safeConnectionDisplay';
import { exportToastDescription } from './transferToastText';

export function ConnectionsExportPanel() {
  const { track } = useAnalytics();
  const connections = useConnectionStore((s) => s.connections);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [includeSecrets, setIncludeSecrets] = useState(false);
  const [busy, setBusy] = useState(false);
  const form = useExportPassphraseForm();

  const rows = useMemo<PickRow[]>(
    () =>
      connections.map((c) => {
        const engine = c.engine ?? 'mssql';
        // The SAME description the import panel uses (describeTarget, via the safe-display layer).
        const target = safeConnectionTarget({
          engine,
          server: c.server,
          port: c.port,
          filePath: c.filePath,
          oracleConnectionStringType: c.oracleConnectionStringType,
          oracleTnsAlias: c.oracleTnsAlias,
          oracleTnsAdmin: c.oracleTnsAdmin,
          oracleTnsDescriptor: c.oracleTnsDescriptor,
        });
        const detail = [engine, c.authentication, c.database, c.login]
          .filter(Boolean)
          .join(' · ');
        const row: PickRow = { id: c.id, label: safeConnectionName(c), target, detail };
        // Same description as the import side, from the same function — a
        // discrepancy between what you tick to send and what you tick to
        // receive would be its own trap.
        const transport = describeTransport({
          engine,
          encrypt: c.encrypt,
          ssl: c.ssl,
          trustServerCertificate: c.trustServerCertificate,
          // Oracle's transport depends on HOW the address is resolved, not just
          // on the wallet — omitting these made the export panel describe an
          // Oracle connection differently from the import panel.
          oracleConnectionStringType: c.oracleConnectionStringType,
          oracleTnsDescriptor: c.oracleTnsDescriptor,
          oracleWalletLocation: c.oracleWalletLocation,
        });
        if (transport) {
          row.transport = transport.label;
          if (transport.weak) row.transportWeak = true;
        }
        if (c.sshTunnel?.enabled) {
          row.sshTarget = `${c.sshTunnel.user}@${c.sshTunnel.host}:${c.sshTunnel.port}`;
        }
        if (c.hasStoredPassword) row.note = 'has a saved password';
        return row;
      }),
    [connections],
  );

  const toggle = (id: string, next: boolean) =>
    setSelected((current) => {
      const copy = new Set(current);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const onExport = async (passphrase: string | null) => {
    setBusy(true);
    try {
      const result = await exportConnectionsToFile({
        connectionIds: [...selected],
        ...(passphrase ? { passphrase } : {}),
        suggestedFileName: connectionsFileName(),
      });
      if (!result.success) {
        if (result.cancelled) return;
        toast.error({ title: 'Export failed', description: result.error });
        return;
      }
      // Success only, and no passphrase or endpoint ever reaches a property.
      track('feature:connections_export', {
        with_secrets: result.withSecrets,
        count_bucket: countBucket(result.exported),
      });
      toast.success({
        title: `Exported ${result.exported} connection${result.exported === 1 ? '' : 's'}`,
        description: exportToastDescription(result),
      });
      form.reset();
      setIncludeSecrets(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3" data-testid="connections-export-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Export connections</div>
        {rows.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="connections-export-all">
            {allSelected ? 'Clear all' : 'Select all'}
          </Button>
        ) : null}
      </div>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{CONNECTIONS_EXPORT_CAVEAT}</span>
      </p>

      <ConnectionPickList
        rows={rows}
        selected={selected}
        onToggle={toggle}
        idPrefix="connections-export"
        emptyLabel="No saved connections to export."
      />

      <div className="flex items-start gap-2">
        <Checkbox
          id="connections-export-include-secrets"
          checked={includeSecrets}
          onCheckedChange={(next) => setIncludeSecrets(next === true)}
          data-testid="connections-export-include-secrets"
          className="mt-0.5"
        />
        <Label
          htmlFor="connections-export-include-secrets"
          className="cursor-pointer text-sm font-normal"
        >
          Include saved passwords, protected by a passphrase
        </Label>
      </div>

      <form.Subscribe selector={(state) => state.values}>
        {(values) => {
          const passphrase = includeSecrets ? derivePassphrase(values) : null;
          const blocked = includeSecrets && !passphrase;
          return (
            <>
              {includeSecrets ? (
                <ExportPassphraseFields form={form}>
                  {values.passphrase.length > 0 && !passphrase ? (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="export-passphrase-blocked"
                    >
                      Enter the same passphrase twice, at least{' '}
                      {MIN_TRANSFER_PASSPHRASE_LENGTH} characters, to include passwords.
                    </p>
                  ) : null}
                </ExportPassphraseFields>
              ) : null}
              <Button
                onClick={() => onExport(passphrase)}
                disabled={busy || selected.size === 0 || blocked}
                data-testid="connections-export-button"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                {busy ? 'Exporting…' : 'Export connections…'}
              </Button>
            </>
          );
        }}
      </form.Subscribe>
    </section>
  );
}
